import React, { useEffect, useState } from 'react';
import { FileText, Eye, Clock, Users, Calendar, AlertCircle } from 'lucide-react';
import { transcriptionAPI, transcriptionsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';


interface Transcription {
  media_file_s3_id: string;
  transcription_file_s3_id: string | null;
  file_name: string | null;
  message_text: string | null;
  length_seconds: number | null;
  length_bytes: number;
  extension: string;
  upload_timestamp: string;
  transcription_timestamp: string | null;
  words_count: number | null;
  speakers_count: number | null;
  has_transcription: boolean;
  status: 'completed' | 'processing';
  content?: any;
  // добавлено как необязательное поле, чтобы сборка TS не падала
  file_path?: string;
}

export const TranscriptionPage: React.FC = () => {
  const { user } = useAuth();
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isViewingContent, setIsViewingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTranscriptions();

    const handleTranscriptionReady = (event: Event) => {
      try {
        const custom = event as CustomEvent;
        const { transcriptionId, jobId } = custom.detail || {};
        console.log('Получен transcription:ready', { transcriptionId, jobId });
        loadTranscriptions();

        if (selectedTranscription && selectedTranscription.transcription_file_s3_id === transcriptionId) {
          // если сейчас открыт просмотр той же стенограммы — обновляем содержимое
          viewTranscription(selectedTranscription);
        } else {
          toast.success('Появилась новая стенограмма');
        }
      } catch (err) {
        console.error('Ошибка в обработчике transcription:ready', err);
      }
    };

    window.addEventListener('transcription:ready', handleTranscriptionReady as EventListener);
    return () => {
      window.removeEventListener('transcription:ready', handleTranscriptionReady as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedTranscription]);

  const loadTranscriptions = async () => {
    if (!user?.user_id && !user?.email) {
      setError('Пользователь не авторизован');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const chatId = user.user_id || user.email;
      const response = await transcriptionsAPI.getList(chatId);
      setTranscriptions(response.transcriptions || []);
    } catch (err: any) {
      console.error('Ошибка при загрузке стенограммы:', err);
      setError(err.message || 'Ошибка при загрузке стенограммы');
      toast.error('Ошибка при загрузке стенограмм');
    } finally {
      setIsLoading(false);
    }
  };

  // Генерация .txt в требуемом формате
  const buildTxtFromContent = (content: any) => {
    if (!content || !Array.isArray(content.docs)) return '';
    const lines: string[] = content.docs.map((doc: any) => {
      const speaker = doc.metadata?.speaker || 'UNKNOWN_SPEAKER';
      const startRaw = doc.metadata?.start_time ?? doc.metadata?.start ?? doc.metadata?.timestamp ?? '';
      const endRaw = doc.metadata?.end_time ?? doc.metadata?.end ?? '';

      const start = startRaw !== '' ? formatTime(startRaw) : '';
      const end = endRaw !== '' ? formatTime(endRaw) : '';
      const timePart = start || end ? `[${start}${start && end ? ', ' : ''}${end}]` : '';
      const text = (doc.page_content || '').replace(/\s+/g, ' ').trim();
      return `${speaker} ${timePart}: ${text}`.trim();
    });

    // **Важная правка** — разделяем записи двойным переносом строки
    return lines.join('\n\n');
  };

  const generateAndDownloadTxt = (transcription: Transcription, content: any) => {
    try {
      const txt = buildTxtFromContent(content);
      const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const baseName = `${transcription.file_name}_${transcription.extension}` || transcription.message_text || 'transcript';
      a.download = `${baseName}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Стенограмма ${baseName}.txt сформирована и скачана`);
    } catch (err) {
      console.error('Ошибка при генерации .txt', err);
      toast.error('Ошибка при формировании .txt');
    }
  };

  // Новая функция: скачать .txt прямо из списка (подгрузив JSON)
  const downloadTxtForTranscription = async (transcription: Transcription) => {
    if (!transcription.transcription_file_s3_id) {
      toast.error('Стенограмма еще не готова');
      return;
    }
    try {
      const blob = await transcriptionAPI.download(transcription.transcription_file_s3_id);
      const text = await blob.text();
      let content = {};
      try {
        content = JSON.parse(text);
      } catch (err) {
        console.error('Не удалось распарсить содержимое стенограммы как JSON при скачивании из списка', err);
        content = { docs: [] };
      }
      generateAndDownloadTxt(transcription, content);
    } catch (err) {
      console.error('Ошибка при скачивании стенограммы для формирования .txt', err);
      toast.error('Ошибка при скачивании стенограммы');
    }
  };

  // Преобразование числа/строки секунд в HH:MM:SS.mmm или MM:SS.mmm
  const formatTime = (value: number | string | undefined | null) => {
    if (value === undefined || value === null || value === '') return '';
    const s = Number(value);
    if (Number.isNaN(s)) return String(value);
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = Math.floor(s % 60);
    const ms = Math.round((s - Math.floor(s)) * 1000);
    const hh = hours > 0 ? String(hours).padStart(2, '0') + ':' : '';
    return `${hh}${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
  };

  const getSpeakerHue = (speaker: string) => {
    let hash = 0;
    for (let i = 0; i < speaker.length; i++) {
      hash = speaker.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return Math.abs(hash) % 360;
  };

  // --- новая логика: карта оттенков (hue) для уникальных спикеров текущей стенограммы
  const speakerHueMap: Record<string, number> = React.useMemo(() => {
    if (!selectedTranscription || !selectedTranscription.content || !Array.isArray(selectedTranscription.content.docs)) return {};
    const docs = selectedTranscription.content.docs as any[];
    const unique = Array.from(new Set(docs.map(d => (d.metadata?.speaker || 'UNKNOWN_SPEAKER').toString().trim())));
    const map: Record<string, number> = {};
    unique.forEach((s, idx) => {
      // распределяем оттенки равномерно по кругу 0..360
      map[s] = Math.round((idx * 360) / Math.max(1, unique.length));
    });
    return map;
  }, [selectedTranscription]);

  const getSpeakerStyleByMap = (speaker: string | undefined) => {
    const name = (speaker || 'UNKNOWN').toString().trim();
    const hue = speakerHueMap[name] ?? getSpeakerHue(name);
    const bg = `hsla(${hue}, 70%, 80%, 0.20)`;
    const border = `hsla(${hue}, 70%, 50%, 0.35)`;
    return { backgroundColor: bg, borderLeft: `4px solid ${border}` } as React.CSSProperties;
  };

  // Просмотр стенограмм: загружаем JSON с сервера (внутренняя загрузка) — но не отдаем JSON пользователю
  const viewTranscription = async (transcription: Transcription) => {
    if (!transcription.transcription_file_s3_id) {
      toast.error('Стенограмма ещё не готова');
      return;
    }

    try {
      const blob = await transcriptionAPI.download(transcription.transcription_file_s3_id);
      const text = await blob.text();
      let content = {};
      try {
        content = JSON.parse(text);
      } catch (err) {
        console.error('Не удалось распарсить содержимое стенограммы как JSON', err);
        content = { docs: [] };
      }

      setSelectedTranscription({ ...transcription, content });
      setIsViewingContent(true);

      // Автозагрузка .txt отключена по требованию — если пользователь хочет, он нажимает "Скачать .txt"
    } catch (error) {
      console.error('Ошибка при загрузке содержимого стенограммы:', error);
      toast.error('Ошибка при загрузке содержимого стенограммы');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Б';
    const k = 1024;
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'Неизвестно';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const getStatusBadge = (status: Transcription['status']) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';

    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'processing':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusText = (status: Transcription['status']) => {
    switch (status) {
      case 'completed':
        return 'Завершено';
      case 'processing':
        return 'Обработка';
      default:
        return 'Неизвестно';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-secondary-600">Загрузка стенограмм...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900">Стенограммы</h1>
          <p className="mt-2 text-secondary-600">Просмотр и управление стенограммами встреч</p>
          <div className="mt-4">
            <Link to="/upload" className="btn-primary">
              Загрузить файл
            </Link>
          </div>
        </div>
        <div className="card">
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadTranscriptions} className="btn-primary">
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900">Стенограммы</h1>
        <p className="mt-2 text-secondary-600">Просмотр и управление стенограммами встреч</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div className="card">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-primary-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary-600">Всего стенограмм</p>
              <p className="text-2xl font-semibold text-secondary-900">{transcriptions.length}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary-600">Завершено</p>
              <p className="text-2xl font-semibold text-secondary-900">{transcriptions.filter(t => t.status === 'completed').length}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary-600">В среднем участников</p>
              <p className="text-2xl font-semibold text-secondary-900">{
                (() => {
                    const valid = transcriptions.filter(t => t.speakers_count != null);
                    return valid.length > 0
                      ? (valid.reduce((sum, t) => sum + t.speakers_count!, 0) / valid.length).toFixed(2)
                      : 0;
                })()
              }</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <Link
          to="/upload"
          className="btn-secondary w-full inline-flex items-center justify-center px-5 py-3 text-base rounded-lg shadow-sm transition-colors duration-150"
        >
          <FileText className="h-5 w-5 mr-2 text-secondary-700" />
          <span>Загрузить файл</span>
        </Link>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-secondary-900">Список стенограмм</h2>
          <button onClick={loadTranscriptions} className="btn-secondary text-sm">Обновить</button>
        </div>

        {transcriptions.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">Нет стенограмм</h3>
            <p className="text-secondary-600">Загрузите аудиофайл, чтобы создать первую стенограмму</p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-2">
            {transcriptions.map((transcription) => (
              <div key={transcription.media_file_s3_id} className="border border-secondary-200 rounded-lg p-4">
              {/* Корневой контейнер карточки: на маленьких экранах - column, на sm+ - row */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                {/* Левый блок: иконка + инфо. min-w-0 + truncate, чтобы не выталкивал кнопки */}
                <div className="flex items-center space-x-3 min-w-0 w-full sm:w-auto">
                  <FileText className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-secondary-900 truncate">
                      {`${transcription.file_name}.${transcription.extension}` || transcription.message_text}
                    </p>
                    <div className="flex flex-wrap items-center space-x-4 text-xs text-secondary-600">
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {format(new Date(transcription.upload_timestamp), 'dd MMM yyyy, HH:mm')}
                      </span>
                      {transcription.length_seconds && (
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatDuration(transcription.length_seconds)}
                        </span>
                      )}
                      <span>{formatFileSize(transcription.length_bytes)}</span>
                      {transcription.words_count && (<span>{transcription.words_count} слов</span>)}
                      {transcription.speakers_count && (<span>{transcription.speakers_count} участников</span>)}
                    </div>
                  </div>
                </div>

                {/* Правый блок: статус + кнопки.
                    На маленьких экранах перенесётся под левый блок (mt-3),
                    на sm+ — в одну строку справа. */}
                <div className="flex items-center space-x-3 mt-3 sm:mt-0">
                  <span className={getStatusBadge(transcription.status)}>{getStatusText(transcription.status)}</span>

                  {transcription.status === 'completed' && transcription.transcription_file_s3_id && (
                    <>
                      <button
                        onClick={() => viewTranscription(transcription)}
                        className="btn-secondary flex items-center space-x-1 whitespace-nowrap"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Просмотр</span>
                      </button>

                      <button
                        onClick={() => downloadTxtForTranscription(transcription)}
                        className="btn-secondary whitespace-nowrap flex-shrink-0"
                      >
                        Скачать
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно для просмотра стенограммы (центруем и уменьшаем высоту) */}
      {isViewingContent && selectedTranscription && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden p-4"
        >
          <div
            className="bg-white rounded-lg max-w-7xl w-[95vw] overflow-hidden shadow-lg"
            // чтобы модал не заходил за низ экрана и сверху/снизу было одинаковое расстояние:
            style={{ maxHeight: 'calc(100vh - 4rem)' }}
          >
            <div className="flex items-center justify-between p-6 border-b border-secondary-200">
              <div>
                <h2 className="text-xl font-semibold text-secondary-900">{`${selectedTranscription.file_name}.${selectedTranscription.extension}` || selectedTranscription.message_text}</h2>
                <div className="text-sm text-secondary-600">{selectedTranscription.speakers_count ? `Участников - ${selectedTranscription.speakers_count}` : ''}</div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    // Скачиваем .txt только при явном нажатии
                    generateAndDownloadTxt(selectedTranscription, selectedTranscription.content);
                  }}
                  className="btn-secondary"
                >
                  Скачать .txt
                </button>

                <button
                  onClick={() => {
                    setIsViewingContent(false);
                    setSelectedTranscription(null);
                  }}
                  className="text-secondary-400 hover:text-secondary-600"
                  aria-label="Закрыть"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Контент с собственной прокруткой; ограничиваем высоту, чтобы не выходило за пределы модала */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 12rem)' }}>
              {selectedTranscription.content && Array.isArray(selectedTranscription.content.docs) ? (
                <div className="space-y-4">
                  {selectedTranscription.content.docs.map((doc: any, index: number) => {
                    const speaker = doc.metadata?.speaker || 'UNKNOWN_SPEAKER';
                    const startRaw = doc.metadata?.start_time ?? doc.metadata?.start ?? doc.metadata?.timestamp ?? '';
                    const endRaw = doc.metadata?.end_time ?? doc.metadata?.end ?? '';
                    const start = startRaw !== '' ? formatTime(startRaw) : '';
                    const end = endRaw !== '' ? formatTime(endRaw) : '';

                    return (
                      <div key={index} className="rounded-md p-4" style={getSpeakerStyleByMap(speaker)}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className="text-sm font-semibold text-primary-800">{speaker}</div>
                            <div className="text-xs text-secondary-600">{start || end ? `${start}${start && end ? ' — ' : ''}${end}` : ''}</div>
                          </div>
                          <div className="text-xs text-secondary-500">{selectedTranscription.file_path || ''}</div>
                        </div>

                        <div className="text-secondary-900 text-base leading-relaxed break-words">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.page_content}</ReactMarkdown>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-secondary-600">Содержимое стенограммы недоступно</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptionPage;
